import React, { useState, useEffect, useRef, Fragment } from 'react';

import { bind } from 'bind-event-listener';

import Button from '@atlaskit/button';
import { colors } from '@atlaskit/theme';

import { useParams } from 'src/router/hooks';

import { styles } from './styles';
import { formatRemovedLine, postCodeSuggestions } from './utils';

interface CodeSuggestionProps {
  isEditMode?: boolean;
  fileMeta?: { content: string | null };
  commentId?: number | null;
}

export const CodeSuggestion = React.memo(function CodeSuggestion({
  isEditMode = false,
  fileMeta = { content: null },
  commentId = null,
}: CodeSuggestionProps) {
  const { repositoryOwner, repositorySlug, pullRequestId } = useParams<{
    repositoryOwner: string;
    repositorySlug: string;
    pullRequestId: string;
  }>();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const lastClickInsideCSExtension = useRef<boolean>(false);

  const removedLine = formatRemovedLine(fileMeta?.content);
  const suggestedLine = `+  if (!content) return null;`;
  const [suggestion, setSuggestion] = useState(suggestedLine);

  const handleSuggestionChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setSuggestion(e.target.value);
  };

  const applySuggestion = () => {
    const message = 'Code suggestion made by user';
    const updatedSuggestion = suggestion.replace('+', '');
    setSuggestion(updatedSuggestion);
    const suggestions = [
      {
        comment_id: commentId,
        content: updatedSuggestion,
      },
    ];
    postCodeSuggestions(
      { message, suggestions },
      repositoryOwner,
      repositorySlug,
      pullRequestId
    );
  };
  useEffect(() => {
    let unbindMousedown: () => void;
    let unbindKeypress: () => void;
    let unbindBlur: () => void;
    let unbindMousedownCapture: () => void;
    let unbindPaste: () => void;

    if (wrapperRef.current) {
      unbindMousedownCapture = bind(wrapperRef.current, {
        type: 'mousedown',
        listener: () => {
          // Add a flag to indicate that the last click was inside the CSExtension. This is used by DOM events that follow this mousedown in order to know if they may have been triggered by a click inside the CSExtension. It is reset after the events have been emitted by the timeout trick.
          lastClickInsideCSExtension.current = true;

          setTimeout(() => {
            lastClickInsideCSExtension.current = false;
          }, 0);
        },
        options: { capture: true },
      });

      unbindMousedown = bind(wrapperRef.current, {
        type: 'mousedown',
        listener: event => {
          event.stopPropagation();
        },
        options: { capture: false },
      });

      unbindKeypress = bind(wrapperRef.current, {
        type: 'keypress',
        listener: event => {
          event.stopPropagation();
          lastClickInsideCSExtension.current = false;
        },
        options: { capture: true },
      });

      unbindPaste = bind(wrapperRef.current, {
        type: 'paste',
        listener: event => {
          event.stopPropagation();
        },
        options: { capture: true },
      });

      unbindBlur = bind(wrapperRef.current, {
        type: 'blur',
        listener: event => {
          // If the last clicked element was inside the CSExtension, force the focus to remain inside the CSExtension.
          // Otherwise, the user has clicked out of the CSExtension and should lose focus.
          const previousElement = event.target as HTMLElement;
          if (lastClickInsideCSExtension.current && previousElement) {
            previousElement.focus();
            lastClickInsideCSExtension.current = false;
          }
        },
        options: { capture: true },
      });
    }

    return () => {
      unbindMousedown?.();
      unbindKeypress?.();
      unbindBlur?.();
      unbindMousedownCapture?.();
      unbindPaste?.();
    };
  }, []);

  return (
    <div>
      {isEditMode ? (
        <div style={{ padding: '10px' }}>
          <div style={styles.line(colors.R50, colors.R500)}>{removedLine}</div>
          <textarea
            value={suggestion}
            onChange={handleSuggestionChange}
            style={styles.textarea}
          />
        </div>
      ) : (
        <Fragment>
          <div style={{ paddingRight: '15px' }}>
            <div style={styles.line(colors.R50, colors.R500)}>
              {removedLine}
            </div>
            <div style={styles.line(colors.G50, colors.G500)}>{suggestion}</div>
          </div>
          <Button appearance="primary" onClick={applySuggestion}>
            Apply Suggestion
          </Button>
        </Fragment>
      )}
    </div>
  );
});
